import {ITableColumn} from '../../types';
import {ReactNode} from 'react';

export type ApplicationDimension = { name: string, value: string }

export enum ApplicationCategory {
    ADMIN = 'ADMIN',
    BUSINESS = 'BUSINESS',
    CONSUMER = 'CONSUMER',
}

export enum ApplicationStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    CREATING = 'CREATING',
    FAILED = 'FAILED',
    // In-flight teardown. Present in the API schema since FNP-77 but never
    // assigned by any code path until the FNP-998 deprovision flow.
    DELETING = 'DELETING',
    // FNP-998: terminal. The application's infrastructure has been torn down.
    // The record is kept for provenance and renamed, so the original name
    // becomes available for reuse.
    DECOMMISSIONED = 'DECOMMISSIONED',
}

export enum BusinessDomain {
    SHARED_SERVICES = 'SHARED_SERVICES',
    BILLING = 'BILLING',
    DOMAINS = 'DOMAINS',
    MARKETPLACE = 'MARKETPLACE',
}

export enum ApplicationType {
    STATIC_SITE = 'STATIC_SITE',
}

export type ApplicationStageStringMap = {
    dev: string,
    test: string,
    prod: string,
}

export type ApplicationProgress = {
    currentStep: string,
    completedSteps: string[],
    failedStep?: string | null,
}

export type RollbackError = {
    step: string,
    error: string,
}

export type RollbackSummary = {
    rolledBackSteps: string[],
    rollbackErrors: RollbackError[],
}

export type ApplicationTag = {
    key: string,
    value: string,
}


export type Application = {
    id: string,
    name?: string,
    description?: string,
    url?: string,
    logoUrl?: string
    applicationCategory?: ApplicationCategory,
    applicationStatus?: ApplicationStatus,
    createdByUsername?: string,
    createdBy?: { givenName: string, familyName: string },
    updatedByUsername?: string
    updatedBy?: { givenName: string, familyName: string },
    created?: string,
    updated?: string,

    // FNP-79 / FNP-76 programmatic-provisioning fields. All optional so
    // legacy admin-create records continue to round-trip unchanged.
    displayName?: string,
    businessDomain?: BusinessDomain,
    applicationType?: ApplicationType,
    parentDomainName?: string,
    // FNP-287: optional leftmost subdomain label. Empty/undefined means
    // apex deployment under the parent domain. Hostname composed as:
    //   subdomain ? `${subdomain}.${stage-prefixed-parent}` : `${stage-prefixed-parent}`
    // (where stage-prefixed-parent is `dev.flexi.photo` etc. for dev/test
    // or bare `flexi.photo` for prod, per flexi-domains-api targetDomainFor.)
    subdomain?: string,
    // FNP-1489: the addressing facts stated by the admin, for a zone the
    // platform lookup cannot reach.
    //
    // The lookup resolves the zone account from a hardcoded {dev,test,prod}
    // map in flexi-domains-api, so an application whose zone lives in any
    // other account cannot be provisioned at all — it fails before the first
    // repository is created. Supplying these skips the lookup entirely.
    //
    // ⚠️ ALL THREE OR NONE. A partial set is refused by the orchestrator
    // rather than merged with a lookup, which would resolve the missing half
    // from a different account.
    //
    // ⚠️ `hostedZoneName` is the zone's own apex (`testing.flexi.network`),
    // NOT `parentDomainName`. The app's hostname is composed from it directly,
    // with no stage prefix — that is the whole point, since the stage-prefix
    // convention above is what makes a zone like this unreachable.
    hostedZoneId?: string,
    hostedZoneName?: string,
    certificateArn?: string,
    deploymentAccountGroupId?: string,
    stageDomains?: ApplicationStageStringMap,
    stageHostedZoneIds?: ApplicationStageStringMap,
    stageCertificateArns?: ApplicationStageStringMap,
    sourceRepositoryName?: string,
    infraRepositoryName?: string,
    sourcePipelineId?: string,
    infraPipelineId?: string,
    infraStackNames?: ApplicationStageStringMap,
    // FNP-1015: the S3 bucket holding each environment's deployed content.
    // Recorded at create alongside `infraStackNames` so both teardown inputs
    // share one encoding — but only for applications provisioned after the
    // field existed. The teardown's EmptyAppBucket step treats a missing name
    // as non-fatal and SKIPS, so an unrecorded bucket is not an error here; it
    // is a non-empty bucket at DeleteInfraStack time and a DELETE_FAILED stack.
    // Deliberately never derived — see the form copy.
    deploymentBucketNames?: ApplicationStageStringMap,
    // FNP-1013: an `Execution.id` from flexinet-workflows-api.
    //
    // Replaced `workflowId`, which was dropped from the schema in step 3. That
    // name asserted a `Workflow` entity this platform has never had —
    // workflows-api defines Playbook / Phase / Step / Execution and nothing
    // else — and it was a live instance of the id-conflation class
    // API_ENTITY_SEAM_MAP §2 calls the platform's most expensive recurring bug.
    provisioningExecutionId?: string,
    stepFunctionsExecutionArn?: string,
    statusReason?: string,
    statusUpdatedAt?: string,
    progress?: ApplicationProgress,
    rollbackSummary?: RollbackSummary,
    tags?: ApplicationTag[],
}


export type ApplicationRecordFormProps = {
    record: Application,
    handleSubmit: (values: Application) => void
    handleCancel: () => void
}

export interface IApplicationTableColumn extends ITableColumn {
    render?: (item: Application) => ReactNode;
}
